import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API_URL = 'https://aamirsaba.com/api';

const About = () => {
  const [aboutData, setAboutData] = useState({
    companyName: 'Aamir Saba',
    tagline: 'Digital Transformation & IT Solutions',
    description: 'Aamir Saba is a premier provider of digital transformation, IT training, and automation solutions. With a global presence in the United Kingdom and United States, we help businesses leverage technology for sustainable growth.',
    mission: 'Our mission is to empower businesses with cutting-edge technology solutions, expert training programs, and automation tools that drive efficiency and growth.',
    ukRegistered: true,
    usaRegistered: true,
    yearsExperience: '4+',
    happyClients: '50+',
    servicesCount: '12+',
    coreServices: ['Power BI', 'ITIL 4 Certification', 'Custom Software Development', 'WhatsApp Automation']
  });
  const [loading, setLoading] = useState(true);

  // LOAD about data from API
  useEffect(() => {
    axios.get(`${API_URL}/about`)
      .then(response => {
        console.log('About data loaded:', response.data);
        const data = response.data;
        let coreServices = data.coreServices;
        if (typeof coreServices === 'string') {
          coreServices = coreServices.split(',').map(s => s.trim());
        }
        if (!Array.isArray(coreServices) || coreServices.length === 0) {
          coreServices = ['Power BI', 'ITIL 4 Certification', 'Custom Software Development', 'WhatsApp Automation'];
        }
        setAboutData({
          companyName: data.companyName || 'Aamir Saba',
          tagline: data.tagline || 'Digital Transformation & IT Solutions',
          description: data.description || '',
          mission: data.mission || '',
          ukRegistered: data.ukRegistered === 1 || data.ukRegistered === true,
          usaRegistered: data.usaRegistered === 1 || data.usaRegistered === true,
          yearsExperience: data.yearsExperience || '4+',
          happyClients: data.happyClients || '50+',
          servicesCount: data.servicesCount || '12+',
          coreServices: coreServices
        });
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading about data:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <section className="pt-32 pb-20 bg-darkNavy min-h-screen">
        <div className="container mx-auto px-6 text-center">
          <div className="text-cyan text-xl">Loading...</div>
        </div>
      </section>
    );
  }

  // Format core services for display
  const formatCoreServices = (services) => {
    if (!services || services.length === 0) return null;
    
    if (services.length === 1) {
      return <strong className="text-cyan">{services[0]}</strong>;
    }
    
    const lastService = services[services.length - 1];
    const otherServices = services.slice(0, -1);
    
    return (
      <span>
        {otherServices.map((service, index) => (
          <span key={index}>
            <strong className="text-cyan">{service}</strong>
            {index < otherServices.length - 1 ? ', ' : ' '}
          </span>
        ))}
        {otherServices.length > 0 && 'and '}
        <strong className="text-cyan">{lastService}</strong>
      </span>
    );
  };

  return (
    <section className="pt-32 pb-20 bg-darkNavy min-h-screen">
      <div className="container mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold text-center">
            About <span className="text-cyan">{aboutData.companyName}</span>
          </h1>
          <p className="text-gray-400 text-center mt-2 text-lg">{aboutData.tagline}</p>
          
          <div className="mt-12 space-y-8 text-gray-300 leading-relaxed">
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {aboutData.ukRegistered && (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan/10 text-cyan rounded-full text-sm font-semibold">
                  🇬🇧 UK Registered Company
                </span>
              )}
              {aboutData.usaRegistered && (
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan/10 text-cyan rounded-full text-sm font-semibold">
                  🇺🇸 USA Registered Company
                </span>
              )}
            </div>

            <p className="text-lg leading-relaxed">{aboutData.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12">
              <div className="bg-navy p-6 rounded-xl text-center border border-gray-800">
                <div className="text-3xl font-bold text-cyan">{aboutData.yearsExperience}</div>
                <div className="text-sm text-gray-400">Years of Excellence</div>
              </div>
              <div className="bg-navy p-6 rounded-xl text-center border border-gray-800">
                <div className="text-3xl font-bold text-cyan">{aboutData.happyClients}</div>
                <div className="text-sm text-gray-400">Happy Clients</div>
              </div>
              <div className="bg-navy p-6 rounded-xl text-center border border-gray-800">
                <div className="text-3xl font-bold text-cyan">{aboutData.servicesCount}</div>
                <div className="text-sm text-gray-400">Services</div>
              </div>
            </div>

            <p className="text-lg leading-relaxed">{aboutData.mission}</p>
            
            {aboutData.coreServices && aboutData.coreServices.length > 0 && (
              <div className="mt-6 p-6 bg-navy rounded-xl border border-gray-800">
                <p className="text-gray-300 text-lg">
                  From {formatCoreServices(aboutData.coreServices)}, we provide end-to-end solutions tailored to your needs.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;